'use client';
import Image from 'next/image';
import { SquareChevronLeft, SquareChevronRight } from 'lucide-react';
import Card from './result/card';
import {
  ShadowbanTaskResult,
  ShadowbanUserPostResult,
} from './result/type';
import Container from './result/Container';
import { useMemo, useState } from 'react';
import { cn, getProxyImage } from '@/lib/utils';
import Tooltip from './result/ToolTip';
import PotentialRisks from './potential-risks';
import { useTranslation } from 'next-i18next';
import styles from './search-result.module.less';

function tranfromNumber(number?: number) {
  const num = Number(number);
  if (!num) return 0;
  if (num < 1000) return num.toLocaleString();
  if (num < 1000_000) return `${Math.floor(num / 100) / 10}K`;
  if (num < 1000_000_000) return `${Math.floor(num / 100_000) / 10}M`;
  return `${Math.floor(num / 100_000_000) / 10}B`;
}
const platforms = {
  twitter: {
    logo: '/images/social/x.svg',
    name: 'Tweets',
  },
  instagram: {
    logo: '/images/social/instagram.svg',
    name: 'Posts',
  },
  tiktok: {
    logo: '/images/social/tiktok.svg',
    name: 'Likes',
  },
};

export default function SearchResult({
  data,
}: {
  data?: ShadowbanTaskResult;
}) {
  const { t } = useTranslation();
  const { task, suggest_ban, search_ban, ghost_ban_list: ghostBanList } =
    data || {};
  const totalPage = Math.ceil(Number(ghostBanList?.length) / 2);
  const [currentPage, setCurrent] = useState(1);
  const currentList = useMemo(() => {
    return (ghostBanList || []).slice((currentPage - 1) * 2, currentPage * 2);
  }, [currentPage, ghostBanList, totalPage]);

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrent(currentPage - 1);
    }
  };
  const handleNext = () => {
    if (currentPage < totalPage) {
      setCurrent(currentPage + 1);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.userHeader}>
        <div className={styles.userInfo}>
          <div className={styles.avatar}>
            {task?.image && (
              <img
                src={getProxyImage(task?.image, task?.platform === 'x' ? 'twitter' : task?.platform)}
                width={78}
                height={78}
                alt=""
              />
            )}
          </div>
          <div className={styles.userDetails}>
            <div className={styles.userName}>
              {task?.name}
            </div>
            <div className={styles.userHandle}>
              <Image
                className={styles.platformIcon}
                src={platforms[task?.platform === 'x' ? 'twitter' : task?.platform || 'twitter'].logo}
                width={20}
                height={20}
                alt=""
              />
              <span className={styles.handle}>
                {`@${task?.username}`}
              </span>
              {task?.is_verified && (
                <Image
                  className={styles.verifiedIcon}
                  src="/images/mark-icon.svg"
                  width={17}
                  height={17}
                  alt="verified"
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles.userStats}>
          <div className={styles.statItem}>
            {tranfromNumber(task?.post_count)}
            <span className={styles.statLabel}>
              {t(platforms[task?.platform === 'x' ? 'twitter' : task?.platform || 'twitter'].name)}
            </span>
          </div>
          <div className={styles.statItem}>
            {tranfromNumber(task?.follower_count)}
            <span className={styles.statLabel}>
              Followers
            </span>
          </div>
          <div className={styles.statItem}>
            {tranfromNumber(task?.following_count)}
            <span className={styles.statLabel}>
              Following
            </span>
          </div>
        </div>
      </div>

      <div className={styles.protectionScore}>
        <h2 className={styles.scoreTitle}>
          {t('Shadowban Protection Score')}
        </h2>
        <div className={cn(
          styles.scoreValue,
          task?.risk === 'Low' ? styles.low : styles.high
        )}>
          <span>
            {t(task?.risk)}
          </span>
        </div>
      </div>
      <div className={styles.searchBanSection}>
        <h2 className={styles.sectionTitle}>{t('Search Ban')}</h2>
        <div className={styles.banCards}>
          <div className={styles.banCard}>
            <div className={styles.cardContent}>
              <span className={styles.emoji}>👁</span>
              <span className={styles.cardLabel}>
                {t('Search Suggestion Ban')}
              </span>
            </div>
            <div className={cn(
              styles.cardStatus,
              suggest_ban === 'Low' ? styles.low : styles.high
            )}>
              {t(suggest_ban)}
            </div>
          </div>
          <div className={styles.banCard}>
            <div className={styles.cardContent}>
              <span className={styles.emoji}>🔍</span>
              <span className={styles.cardLabel}>
                {t('Search Ban')}
              </span>
            </div>
            <div className={cn(
              styles.cardStatus,
              search_ban === 'Low' ? styles.low : styles.high
            )}>
              {t(search_ban)}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.ghostBanSection}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {t('Ghost Ban')}
            {Number(ghostBanList?.length) > 4 && (
              <span className={styles.pageInfo}>
                ({currentPage}/{totalPage})
              </span>
            )}
            <Tooltip content={t('Ghost Ban:Also known as a conventional shadowban, this happens when your posts are hidden from others while everything looks normal on your side. It often includes a search ban, making your content harder to find. Common triggers include excessive posting or following, and the restriction is usually temporary.')}>
              <span className={styles.tooltipWrapper}>
                ?
              </span>
            </Tooltip>
          </h2>
          <div className={styles.navigation}>
            <SquareChevronLeft
              size={26}
              className={cn(
                styles.navIcon,
                currentPage === 1 && styles.disabled
              )}
              onClick={handlePrev}
            />
            <SquareChevronRight
              size={26}
              className={cn(
                styles.navIcon,
                (currentPage === totalPage || totalPage === 0 || currentList?.length === 0) && styles.disabled
              )}
              onClick={handleNext}
            />
          </div>
        </div>
        <Container
          isFeedback={!ghostBanList?.length}
          feedback={
            <div className={styles.feedback}>
              <div className={styles.warningIcon}>
                !
              </div>
              {t('No posts found for this account. Publish a post to check for ghost bans.')}
            </div>
          }
        >
          <div className={styles.cardsGrid}>
            {currentList?.map((item: ShadowbanUserPostResult) => (
              <Card
                key={item.post_id}
                data={item}
                platform={task?.platform === 'x' ? 'twitter' : task?.platform || 'twitter'}
              />
            ))}
          </div>
        </Container>
        <div className={styles.mobileNavigation}>
          <button
            className={styles.mobileNavButton}
            onClick={handlePrev}
            disabled={currentPage === 1 || currentList?.length === 0}
          >
            {t('Previous')}
          </button>
          <button
            className={styles.mobileNavButton}
            onClick={handleNext}
            disabled={currentPage === totalPage || totalPage === 0 || currentList?.length === 0}
          >
            {t('Next')}
          </button>
        </div>
        <PotentialRisks />
      </div>
    </div>
  );
}
